import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ordersApi } from '../api/orders';
import { toErrorMessage } from '../api/client';
import { useStores } from '../auth/StoreContext';
import { Card, PageHead, StatusBadge, OrderTypeBadge } from '../components/ui';
import { Empty, ErrorState, Loading } from '../components/DataStates';
import { StoreSelector } from '../components/StoreSelector';
import {
  DateRangePicker,
  computeRange,
  type DateRange,
} from '../components/DateRangePicker';
import { dateTime, money, startOfDayISO, endOfDayISO } from '../lib/format';

export function BillsPage() {
  const { selectedStoreId } = useStores();
  const [range, setRange] = useState<DateRange>(computeRange('today'));
  const [search, setSearch] = useState('');

  const q = useQuery({
    queryKey: [
      'bills', selectedStoreId,
      range.from, range.to, range.fromTime, range.toTime, search,
    ],
    enabled: selectedStoreId != null,
    queryFn: () =>
      ordersApi.list({
        storeId: selectedStoreId,
        // Honor the custom start/end times when the user set them; otherwise
        // fall back to the whole day.
        from: range.fromTime
          ? `${range.from}T${range.fromTime}:00`
          : startOfDayISO(range.from),
        to: range.toTime
          ? `${range.to}T${range.toTime}:59`
          : endOfDayISO(range.to),
        q: search || undefined,
      }),
  });

  return (
    <>
      <PageHead
        title="Bills"
        subtitle="Browse, search and reprint bills for the selected store."
        actions={<StoreSelector />}
      />

      <Card>
        <div className="row gap-sm flex-wrap" style={{ marginBottom: 16 }}>
          <DateRangePicker value={range} onChange={setRange} />
          <input
            placeholder="Search bill no. or mobile…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 'auto', minWidth: 240 }}
          />
        </div>

        {selectedStoreId == null ? (
          <Empty icon="🏬" title="Select a store" hint="Choose a store to view its bills." />
        ) : q.isLoading ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} />
        ) : !q.data || q.data.length === 0 ? (
          <Empty icon="🧾" title="No bills found" hint="Try a different date range." />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Bill No.</th>
                  <th>Date / Time</th>
                  <th>Type</th>
                  <th>Cashier</th>
                  <th>Mobile</th>
                  <th className="text-right">Total</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((o) => (
                  <tr key={o.orderId}>
                    <td><strong>{o.billNumber}</strong></td>
                    <td>{dateTime(o.createdAt)}</td>
                    <td><OrderTypeBadge type={o.orderType} /></td>
                    <td>{o.cashierName}</td>
                    <td>{o.customerMobile || '—'}</td>
                    <td className="text-right">{money(o.grandTotal)}</td>
                    <td>{o.paymentMethod}</td>
                    <td><StatusBadge status={o.status} /></td>
                    <td className="text-right">
                      <Link className="btn btn-secondary btn-sm" to={`/bills/${o.orderId}`}>
                        View / Reprint
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
